import { useEffect, useRef } from "react";

function DotGrid() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animFrame: number;
    let time = 0;

    const isMobile = window.innerWidth < 768;
    const spacing = isMobile ? 40 : 28;
    const baseAlpha = isMobile ? 0.06 : 0.08;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      time += 0.003;
      const w = window.innerWidth;
      const h = window.innerHeight;
      ctx.clearRect(0, 0, w, h);

      const cols = Math.ceil(w / spacing) + 1;
      const rows = Math.ceil(h / spacing) + 1;

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const x = c * spacing;
          const y = r * spacing;

          const distX = (x - w / 2) / w;
          const distY = (y - h / 2) / h;
          const dist = Math.sqrt(distX * distX + distY * distY);

          const wave = Math.sin(time * 2 + dist * 8) * 0.5 + 0.5;
          const alpha = baseAlpha * (0.3 + wave * 0.7) * (1 - dist * 0.6);

          if (alpha <= 0.01) continue;

          ctx.beginPath();
          ctx.arc(x, y, 1, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(245, 147, 59, ${alpha})`;
          ctx.fill();
        }
      }

      animFrame = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animFrame);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      aria-hidden="true"
    />
  );
}

function AuroraGradients() {
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden" aria-hidden="true">
      <div className="landing-aurora-orb landing-aurora-orb-1" />
      <div className="landing-aurora-orb landing-aurora-orb-2" />
      <div className="landing-aurora-orb landing-aurora-orb-3" />
    </div>
  );
}

function NoiseOverlay() {
  return (
    <div
      className="fixed inset-0 pointer-events-none z-[1] opacity-[0.035]"
      aria-hidden="true"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundRepeat: "repeat",
        backgroundSize: "128px 128px",
      }}
    />
  );
}

export function BackgroundEffects() {
  return (
    <>
      <DotGrid />
      <AuroraGradients />
      <NoiseOverlay />
    </>
  );
}

export function SectionDivider({ className }: { className?: string }) {
  return (
    <div className={`relative w-full h-px ${className || ""}`}>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-vl-orange/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-vl-orange/20 to-transparent blur-sm" />
    </div>
  );
}
